import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

const Header = ({ onShowRegistrasi }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };

    if (showProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    setShowProductDropdown(false);
    // Jika tidak di halaman home, redirect ke home dulu
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const goToMemberArea = () => {
    // Cek apakah ada session/token di localStorage
    const authToken = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    
    // Tutup dropdown jika terbuka
    setShowProductDropdown(false);
    
    // Buka di tab baru
    if (authToken) {
      // Jika sudah login, buka member-area di tab baru
      window.open('/member-area', '_blank');
    } else {
      // Jika belum login, buka login di tab baru
      window.open('/login', '_blank');
    }
  };

  const goToHome = () => {
    navigate('/');
  };

  const goToCatalog = () => {
    navigate('/katalog');
  };

  const goToMitraArea = () => {
    // Cek apakah ada session/token di localStorage
    const authToken = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    
    // Tutup dropdown jika terbuka
    setShowProductDropdown(false);
    
    // Buka di tab baru
    if (authToken) {
      // Jika sudah login, buka mitra-area di tab baru
      window.open('/mitra-area', '_blank');
    } else {
      // Jika belum login, buka login di tab baru
      window.open('/login', '_blank');
    }
  };

  const toggleProductDropdown = () => {
    setShowProductDropdown(!showProductDropdown);
  };

  return (
    <header 
      id="header" 
      className={`fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-[60] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between py-3">
          <Logo 
            size="large" 
            showText={false}
            onClick={goToHome}
          />
          
          <nav className="hidden lg:flex items-center space-x-8">
            <span onClick={() => scrollToSection('beranda')} className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer">Beranda</span>
            <span onClick={() => scrollToSection('tentang')} className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer">Tentang Kami</span>
            
            {/* Dropdown Produk & Layanan */}
            <div 
              ref={dropdownRef}
              className="relative"
            >
              <span 
                onClick={toggleProductDropdown}
                className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer flex items-center"
              >
                Produk & Layanan
                <i className={`fa-solid fa-chevron-down ml-2 text-sm transition-transform ${showProductDropdown ? 'rotate-180' : ''}`}></i>
              </span>
              
              {/* Dropdown Menu */}
              {showProductDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl py-3 border border-gray-100 z-50">
                  <span 
                    onClick={goToCatalog} 
                    className="block px-6 py-3 text-gray-700 hover:bg-accent/10 hover:text-primary font-medium transition-colors cursor-pointer"
                  >
                    <i className="fa-solid fa-box mr-3 text-accent"></i>
                    Katalog Sapi
                  </span>
                  <span 
                    onClick={goToMemberArea} 
                    className="block px-6 py-3 text-gray-700 hover:bg-accent/10 hover:text-primary font-medium transition-colors cursor-pointer border-t border-gray-100"
                  >
                    <i className="fa-solid fa-user-circle mr-3 text-accent"></i>
                    Member Area
                  </span>
                  <span 
                    onClick={goToMitraArea} 
                    className="block px-6 py-3 text-gray-700 hover:bg-accent/10 hover:text-primary font-medium transition-colors cursor-pointer border-t border-gray-100"
                  >
                    <i className="fa-solid fa-handshake mr-3 text-accent"></i>
                    Mitra Area
                  </span>
                </div>
              )}
            </div>

            <span onClick={() => scrollToSection('visi-misi')} className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer">Visi & Misi</span>
            <span onClick={() => scrollToSection('keunggulan')} className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer">Keunggulan</span>
            <span onClick={() => scrollToSection('kontak')} className="text-gray-700 hover:text-primary font-medium transition-colors cursor-pointer">Kontak</span>
          </nav>

          <div className="flex items-center space-x-3">
            <button 
              onClick={onShowRegistrasi} 
              className="hidden sm:inline-flex bg-primary text-white px-4 sm:px-6 py-2 rounded-full font-semibold hover:bg-primary/90 transition-colors"
            >
              Registrasi
            </button>
            <button 
              onClick={() => scrollToSection('kontak')} 
              className="hidden sm:inline-flex bg-accent text-primary px-4 sm:px-6 py-2 rounded-full font-semibold hover:bg-yellow-400 transition-colors"
            >
              Hubungi Kami
            </button>
            <button 
              className="lg:hidden text-primary"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Buka menu navigasi"
            >
              <i className="fa-solid fa-bars text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`lg:hidden fixed inset-0 bg-white transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      ></div>
      <div
        className={`lg:hidden fixed top-0 right-0 bottom-0 w-full bg-white transition-transform duration-300 z-[70] ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="h-screen w-full flex flex-col bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <Logo size="small" showText={false} onClick={() => { goToHome(); setIsMobileMenuOpen(false); }} />
            <button
              className="text-gray-600 hover:text-primary transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Tutup menu navigasi"
            >
              <i className="fa-solid fa-xmark text-2xl"></i>
            </button>
          </div>
          <nav className="px-6 py-6 space-y-4 flex-1 pb-24 overflow-y-auto bg-white">
            <button onClick={() => scrollToSection('beranda')} className="block w-full text-left text-gray-700 font-semibold py-3 border-b border-gray-100">
              Beranda
            </button>
            <button onClick={() => scrollToSection('tentang')} className="block w-full text-left text-gray-700 font-semibold py-3 border-b border-gray-100">
              Tentang Kami
            </button>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Produk & Layanan</p>
              <button onClick={() => { goToCatalog(); setIsMobileMenuOpen(false); }} className="block w-full text-left text-gray-700 font-semibold py-3 border border-gray-100 rounded-xl px-4">
                <i className="fa-solid fa-box mr-2 text-accent"></i>
                Katalog Sapi
              </button>
              <button onClick={() => { goToMemberArea(); setIsMobileMenuOpen(false); }} className="block w-full text-left text-gray-700 font-semibold py-3 border border-gray-100 rounded-xl px-4">
                <i className="fa-solid fa-user-circle mr-2 text-accent"></i>
                Member Area
              </button>
              <button onClick={() => { goToMitraArea(); setIsMobileMenuOpen(false); }} className="block w-full text-left text-gray-700 font-semibold py-3 border border-gray-100 rounded-xl px-4">
                <i className="fa-solid fa-handshake mr-2 text-accent"></i>
                Mitra Area
              </button>
            </div>
            <button onClick={() => scrollToSection('visi-misi')} className="block w-full text-left text-gray-700 font-semibold py-3 border-b border-gray-100">
              Visi & Misi
            </button>
            <button onClick={() => scrollToSection('keunggulan')} className="block w-full text-left text-gray-700 font-semibold py-3 border-b border-gray-100">
              Keunggulan
            </button>
            <button onClick={() => scrollToSection('kontak')} className="block w-full text-left text-gray-700 font-semibold py-3 border-b border-gray-100">
              Kontak
            </button>
            <div className="pt-4 space-y-3">
              <button
                onClick={() => { onShowRegistrasi(); setIsMobileMenuOpen(false); }}
                className="w-full bg-primary text-white px-4 py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors"
              >
                Registrasi
              </button>
              <button
                onClick={() => { scrollToSection('kontak'); }}
                className="w-full bg-accent text-primary px-4 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-colors"
              >
                Hubungi Kami
              </button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;

